import React from 'react'
import { useDispatch, addNotification } from '@ggs/store'
import { useI18n } from '@ggs/i18n'
import { get, first } from 'lodash'
import { useApiRequest } from './'

/**
 * @typedef {import('../../definitions').AcademicPeriodListParams} AcademicPeriodListParams
 * @typedef {import('../../definitions').AcademicPeriodListResponse} AcademicPeriodListResponse
 * @typedef {import('../../definitions').AcademicPeriodRecord} AcademicPeriodRecord
 * @typedef {import('../../definitions').CourseListResponse} CourseListResponse
 * @typedef {import('../../definitions').CourseListParams} CourseListParams
 * @typedef {import('../../definitions').CourseSectionsListParams} CourseSectionsListParams
 * @typedef {import('../../definitions').CourseSectionsListResponse} CourseSectionsListResponse
 * @typedef {import('../../definitions').CourseRecord} CourseRecord
 * @typedef {import('../../definitions').ProgramStudyRecord} ProgramStudyRecord
 * @typedef {import('../../definitions').CourseSubjectsListParams} CourseSubjectListParams
 * @typedef {import('../../definitions').CourseSubjectsListResponse} CourseSubjectsListResponse
 * @typedef {import('../../definitions').CourseSubjectRecord} CourseSubjectRecord
 * @typedef {import('../../definitions').AcademicUnitHierarchiesListParams} AcademicUnitHierarchiesListParams
 * @typedef {import('../../definitions').AcademicUnitHierarchiesListResponse} AcademicUnitHierarchiesListResponse
 * @typedef {import('../../definitions').StandardTimetablesListParams} StandardTimetablesListParams
 * @typedef {import('../../definitions').StandardTimetableListResponse} StandardTimetableListResponse
 * @typedef {import('../../definitions').StandardTimetableRecord} StandardTimetableRecord
 */

/**
 * @typedef {(params: AcademicPeriodListParams) => Promise<AcademicPeriodListResponse>} GetAcademicPeriods
 * @typedef {(params: AcademicUnitHierarchiesListParams) => Promise<AcademicUnitHierarchiesListResponse>} GetAcademicUnitHierarchies
 * @typedef {(params: CourseListParams) => Promise<CourseListResponse>} GetCourses
 * @typedef {(params: CourseSectionsListParams) => Promise<CourseSectionsListResponse>} GetCourseSections
 * @typedef {(params: CourseSubjectListParams) => Promise<CourseSubjectsListResponse>} GetCourseSubjects
 * @typedef {(id: string) => Promise<CourseRecord>} GetCourse
 * @typedef {(id: string) => Promise<ProgramStudyRecord>} GetProgramStudy
 * @typedef {(id: string) => Promise<StandardTimetableRecord>} GetStandardTimetable
 * @typedef {(params: StandardTimetablesListParams) => Promise<StandardTimetableListResponse>} GetStandardTimetables
 */

/**
 * @typedef {Object} LocationApiHook
 * @property {GetAcademicPeriods} getAcademicPeriods
 * @property {GetAcademicUnitHierarchies} getAcademicUnitHierarchies
 * @property {GetCourseSections} getCourseSections
 * @property {GetCourses} getCourses
 * @property {GetCourse} getCourse
 * @property {GetProgramStudy} getProgramStudy
 * @property {GetCourseSubjects} getCourseSubjects
 * @property {GetStandardTimetables} getStandardTimetables
 * @property {GetStandardTimetable} getStandardTimetable
 */

/**
 * Hook that expose functions to consume Location API data.
 * @return {LocationApiHook}
 */
function useAcademicApi() {
  const { t } = useI18n()
  const dispatch = useDispatch()
  const baseUrl = `${process.env.REACT_APP_ACADEMIC_BASE_URL}` || 'http://localhost'
  const { doApiRequest, prepareQueryParams } = useApiRequest({
    baseUrl,
  })

  /**
   * Process a API request.
   * @param {string} endpoint An API endpoint path
   * @param {any} data Payload data
   * @param {Object<string, string>=} customHeaders
   * @param {string} method
   * @return {Promise<any>}
   */
  const doRequest = async (endpoint, data = null, customHeaders, method = '') => {
    const proxyEndpoint = `?_api_proxy_uri=${encodeURIComponent(endpoint)}`
    const response = doApiRequest({
      endpoint: proxyEndpoint,
      data,
      customHeaders,
      method,
    })

    const errorCode = get(response, 'errorCode', '')
    if (errorCode) {
      dispatch(
        addNotification('academic_api_request_failed', 'error', t('Academic API request failed.'))
      )

      return null
    }

    return response
  }

  /**
   * Get UBC campuses list.
   * @type {GetAcademicPeriods}
   * @param {AcademicPeriodListParams} params
   * @return {Promise<AcademicPeriodListResponse>}
   */
  const getAcademicPeriods = async (params) => {
    const endpoint = 'academic-periods'
    const queryParams = prepareQueryParams(params)
    const response = await doRequest(endpoint + '?' + queryParams)

    if (!response?.pageItems) {
      return null
    }

    return response
  }

  /**
   * Get UBC academic periods hierarchies.
   * @type {GetAcademicUnitHierarchies}
   * @param {AcademicUnitHierarchiesListParams} params
   * @return {Promise<AcademicUnitHierarchiesListResponse>}
   */
  const getAcademicUnitHierarchies = async (params) => {
    const endpoint = 'academic-unit-hierarchies'
    const queryParams = prepareQueryParams(params)
    const response = await doRequest(endpoint + '?' + queryParams)

    if (!response?.pageItems) {
      return null
    }

    return response
  }

  /**
   * Get UBC courses list.
   * @type {GetCourses}
   * @param {CourseListParams} params
   * @return {Promise<CourseListResponse>}
   */
  const getCourses = async (params) => {
    const endpoint = 'courses'
    const queryParams = prepareQueryParams(params)
    const response = await doRequest(endpoint + '?' + queryParams)

    if (!response?.pageItems) {
      return null
    }

    return response
  }

  /**
   * Get UBC courses sections.
   * @type {GetCourseSections}
   * @param {CourseSectionsListParams} params
   * @return {Promise<CourseSectionsListResponse>}
   */
  const getCourseSections = async (params) => {
    const endpoint = 'course-sections'
    const queryParams = prepareQueryParams({
      ...params,
      sectionStatus: ['Waitlist', 'Canceled', 'Closed', 'Open'],
      isNotVisible: false,
    })

    const response = await doRequest(endpoint + '?' + queryParams)

    if (!response?.pageItems) {
      return null
    }

    return response
  }

  /**
   * Get course by ID.
   * @type {GetCourse}
   * @param {string} id
   * @return {Promise<CourseRecord>}
   */
  const getCourse = async (id) => {
    const results = await getCourses({ courseInstanceId: [id], useEffectiveDate: 'false' })

    if (!results) {
      return null
    }

    return first(results.pageItems)
  }

  /**
   * Get program of study by ID.
   * @type {GetProgramStudy}
   * @param {string} id
   * @return {Promise<ProgramStudyRecord>}
   */
  const getProgramStudy = async (id) => {
    const endpoint = `programs-of-study/id::${id}`
    const programStudyRecord = await doRequest(endpoint)

    if (!programStudyRecord) {
      return null
    }

    return programStudyRecord
  }

  /**
   * Get course subjects.
   * @type {GetCourseSubjects}
   * @param {CourseSubjectListParams} params
   * @return {Promise<CourseSectionsListResponse>}
   */
  const getCourseSubjects = async (params) => {
    const endpoint = 'course-subjects'
    const queryParams = prepareQueryParams(params)
    const response = await doRequest(endpoint + '?' + queryParams)

    if (!response?.pageItems) {
      return null
    }

    return response
  }

  /**
   * Get standard timetables on given params.
   * @type {GetStandardTimetables}
   * @param {StandardTimetablesListParams} params
   * @return {Promise<StandardTimetableListResponse>}
   */
  const getStandardTimetables = async (params) => {
    const endpoint = 'standard-timetables'
    const queryParams = prepareQueryParams(params)
    const response = await doRequest(`${endpoint}?${queryParams}`)

    if (!response?.pageItems) {
      return null
    }

    return response
  }

  /**
   * Get standard timetable.
   * @type {GetStandardTimetable}
   * @param {string} id
   * @return {Promise<StandardTimetableRecord>}
   */
  const getStandardTimetable = async (id) => {
    const endpoint = `standard-timetables/id::${id}`
    const standardTimetable = await doRequest(endpoint)

    if (!standardTimetable) {
      return null
    }

    return standardTimetable
  }

  return {
    getAcademicPeriods,
    getAcademicUnitHierarchies,
    getCourse,
    getCourseSections,
    getCourseSubjects,
    getCourses,
    getProgramStudy,
    getStandardTimetables,
    getStandardTimetable,
  }
}

export default useAcademicApi
